import { ShieldCheck } from "lucide-react";
import { FaqAccordion } from "@/components/accordion";
import { AppFooter } from "@/components/app-footer";
import { AppHeader } from "@/components/app-header";
import { StartFreeLink } from "@/components/cta-buttons";
import { LocaleLink } from "@/components/locale-link";
import { useTranslation } from "@/common/providers/i18n-provider";
import { csvBankPreset } from "@/config/csv-bank-presets";
import { trackToolUse } from "@/lib/tools-analytics";
import { CSV_BANK_FORMATS } from "./csv-bank-formats";
import { bankFaqKeys, bankStepKeys } from "./csv-bank-lander-content";
import { CsvConverterWidget } from "./csv-converter-widget";
import { DeferredPageShareBar } from "@/components/share/deferred-page-share-bar";
import "@/styles/company-pages.css";
import "@/styles/tools.css";

/** The actual pre-mapped converter, with the bank's researched export guidance intact. */
export function CsvBankLanderPage({ slug }: { slug: string }) {
  const { t, locale } = useTranslation();
  const preset = csvBankPreset(slug);
  const format = CSV_BANK_FORMATS[slug];
  if (!preset || !format) return null;
  const kp = preset.keyPrefix;
  return (
    <div className="company-page tools-page">
      <AppHeader />
      <main id="main">
        <section className="tool-hero">
          <div className="company-container">
            <LocaleLink
              to="/tools/csv-to-beancount"
              className="company-text-link tool-back"
            >
              {t("toolName.csvToBeancount")}
            </LocaleLink>
            <div className="tool-hero-copy">
              <div>
                <p className="tool-context">
                  {t("tools.csvBank.eyebrow", { bank: preset.name })}
                </p>
                <h1>{t(`${kp}.title`)}</h1>
              </div>
              <div>
                <p className="company-lead">{t(`${kp}.summary`)}</p>
                <p className="tool-privacy">
                  <ShieldCheck size={21} aria-hidden="true" />
                  {t("tools.csvBank.verified", { bank: preset.name })}
                </p>
                <p className="tool-local-note">{t("tools.workspace.local")}</p>
                <div className="tool-jumps">
                  <a href="#converter">{t("tools.navigation.converter")}</a>
                  <a href="#export-guide">
                    {t("tools.navigation.instructions")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <CsvConverterWidget preset={format} presetSlug={preset.slug} />
        <section id="export-guide" className="tool-export-guide">
          <div className="company-container">
            <h2>{t("tools.csvBank.exportHeading", { bank: preset.name })}</h2>
            <div className="tool-guide-layout">
              <ol className="tool-export-steps">
                {bankStepKeys(preset).map((key, index) => (
                  <li key={key}>
                    <span aria-hidden="true">
                      {(index + 1).toLocaleString(locale)}
                    </span>
                    <p>{t(key)}</p>
                  </li>
                ))}
              </ol>
              <div className="tool-format-note">
                <h3>{t(`${kp}.quirk.title`)}</h3>
                <p>{t(`${kp}.quirk.body`)}</p>
              </div>
            </div>
          </div>
        </section>
        <section className="tool-faq">
          <div className="company-container">
            <h2>{t("tools.csvBank.faqHeading", { bank: preset.name })}</h2>
            <FaqAccordion
              items={bankFaqKeys(preset).map(([question, answer]) => ({
                id: question,
                question: t(question),
                answer: t(answer),
              }))}
            />
            <LocaleLink
              to="/tools/csv-to-beancount"
              className="company-text-link tool-other-banks"
            >
              {t("tools.csvBank.otherBanks")}
            </LocaleLink>
          </div>
        </section>
        <DeferredPageShareBar
          contentType="tools"
          title={t(`${kp}.title`)}
        />
        <section className="tool-final">
          <div className="company-container">
            <div>
              <h2>{t("tools.csvBank.cta.title")}</h2>
              <p>{t("tools.csvBank.cta.subtitle")}</p>
            </div>
            <div className="tool-actions">
              <StartFreeLink
                surface="tools_csv_bank"
                onClick={() =>
                  trackToolUse("csv_to_beancount", "cta", preset.slug)
                }
              />
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
